import { useQuery } from '@tanstack/react-query';
import { auth } from '../lib/firebase';
import { getCloudRunServiceUrl } from '../api/axios';

/**
 * Generic authed recording proxy fetcher. Pulls audio bytes through a
 * remi-api endpoint that requires a Firebase Bearer token, then returns
 * a Blob URL safe to drop into `<audio src>`. A direct
 * `<audio src={proxyUrl}>` won't work because the browser doesn't send
 * Authorization on media element requests.
 */
const fetchRecordingBlobUrl = async (
  path: string,
  context: string,
): Promise<string> => {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  const url = `${getCloudRunServiceUrl('remi-api')}${path}`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    let detail = '';
    try {
      detail = (await res.text()).slice(0, 200);
    } catch {
      // body unreadable — fine
    }
    console.warn(`[${context}] fetch failed`, {
      url,
      status: res.status,
      statusText: res.statusText,
      detail,
    });
    throw new Error(
      `Recording fetch failed (${res.status} ${res.statusText})${detail ? ` — ${detail}` : ''}`,
    );
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
};

/**
 * Stream a call recording (`GET /api/v1/calls/{call_id}/recording`).
 *
 * Usage:
 *   const { data: audioUrl, isLoading, error } = useCallRecording(callId);
 *   {audioUrl && <audio src={audioUrl} controls />}
 */
export const useCallRecording = (
  callId: string | null | undefined,
  options: { enabled?: boolean } = {},
) => {
  const { enabled = true } = options;
  return useQuery<string, Error>({
    queryKey: ['call-recording', callId],
    enabled: enabled && !!callId,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 30,
    retry: 1,
    queryFn: () =>
      fetchRecordingBlobUrl(
        `/api/v1/calls/${encodeURIComponent(callId!)}/recording`,
        'useCallRecording',
      ),
  });
};

export type LiveTransferRecordingKind = 'recording' | 'voicemail';

/**
 * Stream a live-transfer recording or voicemail through remi-api:
 *   recording → `GET /api/v1/live-transfers/{transfer_id}/recording`
 *   voicemail → `GET /api/v1/live-transfers/{transfer_id}/voicemail`
 */
export const useLiveTransferRecording = (
  transferId: string | null | undefined,
  kind: LiveTransferRecordingKind = 'recording',
  options: { enabled?: boolean } = {},
) => {
  const { enabled = true } = options;
  return useQuery<string, Error>({
    queryKey: ['live-transfer-recording', transferId, kind],
    enabled: enabled && !!transferId,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 30,
    retry: 1,
    queryFn: () =>
      fetchRecordingBlobUrl(
        `/api/v1/live-transfers/${encodeURIComponent(transferId!)}/${kind}`,
        `useLiveTransferRecording:${kind}`,
      ),
  });
};
